import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { dealKeys } from '@/hooks/api/deal-keys'
import { dashboardKeys } from '@/hooks/api/dashboard'
import { eventConflictRange, findEventConflicts } from '@repo/types'
import type { DealStageRef, EventType, DealParties, EntityEventsResponse, EventRecurrenceInfo, RecurrenceScope, CalendarInvite } from '@repo/types'

/**
 * Typed data-access layer for the `/events` endpoints (calendar + viewings).
 *
 * Centralises the query keys, URL building, response envelope, and cache
 * invalidation that were previously hand-rolled inline across CalendarPage and
 * the deal viewing modal. Query keys are preserved verbatim so any remaining ad-hoc
 * `invalidateQueries` calls keep matching:
 *   - agent options → ['event-agent-options']
 *   - calendar range → ['events', year, month, agentId]
 *
 * Mutations invalidate the broad ['events'] prefix (covers every range query and
 * the per-entity history) plus the deal detail (deal.events) and the dashboard
 * (weekViewings); page-specific reactions (modal close, confirm dialogs) stay at
 * the call site via `mutate(vars, { onSuccess })`.
 */

/**
 * Osoba udalosti (priama väzba alebo strana obchodu) — zásuvka v kalendári ukazuje tieto údaje.
 * `customerType`, `source` a `createdAt` posiela API len pri priamej väzbe; strana obchodu
 * (PARTY_CLIENT_SELECT) ich nemá a zásuvka ukáže „—".
 */
export interface EventContactInfo {
  id: string
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  customerType?: 'BUYING' | 'SELLING' | 'RENTING' | 'LETTING' | null
  source?: string | null
  createdAt?: string
}

export interface CalendarEvent {
  id: string
  type: EventType
  status: string
  scheduledAt: string
  duration: number
  /** Celodenná udalosť (import z Google/ICS) — scheduledAt je polnoc UTC, duration násobok 1440. */
  allDay: boolean
  /** Farba udalosti — kľúč z EVENT_COLORS (@repo/types), null = default (blue). */
  color: string | null
  notes: string | null
  /** Link na videohovor (Google Meet a pod.) — importovaný z Google Calendar syncu. */
  meetingUrl: string | null
  feedback: string | null
  rating: number | null
  checkInAt: string | null
  checkOutAt: string | null
  createdAt: string
  customTitle: string | null
  contactName: string | null
  contactPhone: string | null
  /** Súkromná udalosť kolegu — API vrátilo len termín/trvanie/stav; ostatné polia sú null. */
  isPrivate?: boolean
  /** Séria opakovanej udalosti (null = jednorazová). */
  seriesId?: string | null
  /** Súhrn pravidla opakovania (label po slovensky), null bez série. */
  recurrence?: EventRecurrenceInfo | null
  attendees?: { id: string; email: string; name: string | null; userId?: string | null; status?: string; respondedAt?: string | null }[]
  /** Stav pozvánky vlastníka zobrazeného kalendára; null = vlastná udalosť. Virtuálne (EXTERNAL/GCAL) nemajú detail. */
  invite?: CalendarInvite | null
  agent: { id: string; firstName: string; lastName: string } | null
  createdBy: { id: string; firstName: string; lastName: string } | null
  property: { id: number; title: string; city: string | null } | null
  contact?: EventContactInfo | null
  deal?: ({
    id: string
    title: string
    stage: DealStageRef
  } & DealParties<EventContactInfo>) | null
}

export interface EventAgentOption {
  id: string
  firstName: string
  lastName: string
  role: string
  branchId: string | null
  /** Owner, ktorý do mojej pobočky nezdieľa kalendár — v dropdowne so zámkom, disabled. */
  locked: boolean
}

export const eventKeys = {
  /** Prefix matching every calendar range query — use for broad invalidation. */
  all: ['events'] as const,
  agentOptions: ['event-agent-options'] as const,
  range: (year: number, month: number, agentId: string) =>
    ['events', year, month, agentId] as const,
}

/** Agents the current user may filter the calendar by (manager/assistant+). */
export function useEventAgentOptions(enabled: boolean) {
  return useQuery<EventAgentOption[]>({
    queryKey: eventKeys.agentOptions,
    queryFn: () => api.get<EventAgentOption[]>('/events/agent-options').then((r) => r.data),
    enabled,
  })
}

/** Single event by id — deep-link z notifikácie (/calendar?event=<id>). */
export function useEvent(id: string | null) {
  return useQuery<CalendarEvent>({
    queryKey: [...eventKeys.all, 'detail', id],
    queryFn: () => api.get<CalendarEvent>(`/events/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

/** Events for the visible month. `agentId` of 'self' omits the agent filter. */
export function useCalendarEvents(year: number, month: number, agentId: string) {
  return useQuery<CalendarEvent[]>({
    queryKey: eventKeys.range(year, month, agentId),
    queryFn: async () => {
      // 31 dní pred mesiac — viacdňová celodenná udalosť môže začínať v minulom
      // mesiaci a zasahovať do zobrazeného; do dní ju premieta filter v CalendarPage.
      const from = new Date(year, month, 1 - 31).toISOString()
      const to = new Date(year, month + 1, 1).toISOString()
      const agentParam = agentId !== 'self' ? `&agentId=${agentId}` : ''
      const { data } = await api.get<CalendarEvent[]>(`/events?from=${from}&to=${to}${agentParam}`)
      return data
    },
  })
}

/**
 * Udalosti môjho kalendára, ktoré sa prekrývajú s termínom [start, end) —
 * upozornenie vo formulári novej udalosti. Dopyt ide po celých dňoch
 * (eventConflictRange), presný prienik sa počíta lokálne. `null` = termín
 * nie je zadaný / celodenná udalosť → bez kontroly.
 */
export function useEventConflicts(start: Date | null, end: Date | null): CalendarEvent[] {
  const range = start && end ? eventConflictRange(start, end) : null
  const { data } = useQuery<CalendarEvent[]>({
    queryKey: [...eventKeys.all, 'conflicts', range?.from, range?.to],
    queryFn: () => api.get<CalendarEvent[]>(`/events?from=${range!.from}&to=${range!.to}`).then((r) => r.data),
    enabled: !!range,
    staleTime: 30_000,
  })
  return start && end && Array.isArray(data) ? findEventConflicts(data, start, end) : []
}

/** Udalosť sa zobrazuje v kalendári a histórii entít (['events', …]), v detaile obchodu a na dashboarde. */
function invalidateEventViews(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: eventKeys.all })
  qc.invalidateQueries({ queryKey: dealKeys.details })
  qc.invalidateQueries({ queryKey: dashboardKeys.stats })
}

/** Create an event (calendar event or a viewing — type OBHLIADKA). */
export function useCreateEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post('/events', payload),
    onSuccess: () => invalidateEventViews(qc),
  })
}

/** `?scope=` len pri sérii a mimo defaultu `this`. */
function scopeQuery(scope?: RecurrenceScope): string {
  return scope && scope !== 'this' ? `?scope=${scope}` : ''
}

/** Patch an event (status change or inline edit). Pri sérii `scope` = this | following | all. */
export function useUpdateEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data, scope }: { id: string; data: Record<string, unknown>; scope?: RecurrenceScope }) =>
      api.patch(`/events/${id}${scopeQuery(scope)}`, data),
    onSuccess: () => invalidateEventViews(qc),
  })
}

/** Delete an event. Pri sérii `scope` = this | following | all. */
export function useDeleteEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, scope }: { id: string; scope?: RecurrenceScope }) =>
      api.delete(`/events/${id}${scopeQuery(scope)}`),
    onSuccess: () => invalidateEventViews(qc),
  })
}

/** Pridá účastníkov (pozvánka sa pošle len novým). */
export function useAddEventAttendees() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, attendees }: { id: string; attendees: { email: string; name?: string }[] }) =>
      api.post(`/events/${id}/attendees`, { attendees }),
    onSuccess: () => invalidateEventViews(qc),
  })
}

/** Odstráni účastníka (bez emailu). */
export function useRemoveEventAttendee() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, attendeeId }: { id: string; attendeeId: string }) =>
      api.delete(`/events/${id}/attendees/${attendeeId}`),
    onSuccess: () => invalidateEventViews(qc),
  })
}

// ── História udalostí pri kontakte / nehnuteľnosti ──────────────────────────────

export type EntityEventsRef = { contactId: string } | { propertyId: number }

/** `GET /events/for-entity` — súhrn účasti + tenant-wide zoznam udalostí danej entity. */
export function useEntityEvents(ref: EntityEventsRef | null) {
  return useQuery<EntityEventsResponse>({
    queryKey: [...eventKeys.all, 'for-entity', ref],
    queryFn: () => api.get<EntityEventsResponse>('/events/for-entity', { params: ref }).then((r) => r.data),
    enabled: !!ref,
  })
}
